import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import {
  ConfirmationNumberOutlined as TicketIcon,
  CasinoOutlined as CasinoIcon,
  ExploreOutlined as ExploreIcon,
  EmojiEventsOutlined as TrophyIcon,
  InfoOutlined as InfoIcon,
  SportsEsportsOutlined as GameIcon,
  RocketLaunchOutlined as RocketIcon,
  ArrowForwardRounded as ArrowForwardIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { Path, LOTTERY_NAMES } from "../../../../../static/Constants";
import { CommonButton } from "../../../../Components/ui";

/**
 * Revamped Empty / Unavailable State for BuyLottery
 * Displays a modern elevated card with ambient backdrop glow, status pill,
 * contextual guidance, primary/secondary actions, and quick game mode shortcuts.
 */
const BuyLotteryEmptyState = ({ lotteryId, onRetry }) => {
  const history = useHistory();

  const title = lotteryId
    ? `Lottery #${lotteryId} Unavailable`
    : "No Active Lotteries Found";

  const description = lotteryId
    ? "This lottery ticket is either concluded, has reached capacity, or is not currently active on this network."
    : "There are currently no active lottery pools in this category. Explore all live game arenas or check out recent draw results!";

  const quickLinks = [
    { label: LOTTERY_NAMES.SPINBET || "SpinBET", icon: <CasinoIcon sx={{ fontSize: 16 }} />, path: Path.allLotteries || "/all-lotteries" },
    { label: LOTTERY_NAMES.MINEBET || "MineBET", icon: <GameIcon sx={{ fontSize: 16 }} />, path: Path.allminelottery || "/all-minelottery" },
    { label: LOTTERY_NAMES.ROCKETBET || "RocketBET", icon: <RocketIcon sx={{ fontSize: 16 }} />, path: Path.allmissilelottery || "/all-missilelottery" },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        textAlign: "center",
        py: { xs: 5, sm: 6 },
        px: { xs: 3, sm: 5 },
        my: 4,
        bgcolor: colors.background.paper || "#ffffff",
        borderRadius: borderRadiusPx.xxl,
        border: `1px solid ${colors.slate[200]}`,
        boxShadow: customShadows.cardHover || "0 12px 36px -4px rgba(15, 23, 42, 0.08), 0 4px 16px -2px rgba(15, 23, 42, 0.03)",
        maxWidth: 580,
        mx: "auto",
        overflow: "hidden",
        background: `radial-gradient(100% 120% at 50% 0%, ${colors.blue[50]} 0%, #ffffff 70%)`,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 20px 45px -8px rgba(15, 23, 42, 0.12)",
          borderColor: colors.blue[300],
        },
      }}
    >
      {/* Top Accent Gradient Bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)",
        }}
      />

      {/* Status Pill Chip */}
      <Box sx={{ mb: 2.5 }}>
        <Chip
          icon={lotteryId ? <TicketIcon sx={{ fontSize: 16 }} /> : <CasinoIcon sx={{ fontSize: 16 }} />}
          label={lotteryId ? `Draw #${lotteryId} Inactive` : "No Open Pools"}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            px: 1,
            py: 0.5,
            bgcolor: lotteryId ? colors.amber[50] : colors.blue[50],
            color: lotteryId ? colors.amber[700] : colors.primary.main,
            border: `1px solid ${lotteryId ? colors.amber[200] : colors.blue[200]}`,
            "& .MuiChip-icon": {
              color: "inherit",
            },
          }}
        />
      </Box>

      {/* Layered Animated Glowing Icon Badge */}
      <Box
        sx={{
          width: 80,
          height: 80,
          mx: "auto",
          mb: 3,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: lotteryId ? colors.amber[50] : colors.blue[50],
          border: `2px solid ${lotteryId ? colors.amber[200] : colors.blue[200]}`,
          boxShadow: `0 8px 24px ${lotteryId ? "rgba(245, 158, 11, 0.15)" : "rgba(37, 99, 235, 0.15)"}`,
          position: "relative",
        }}
      >
        {lotteryId ? (
          <TicketIcon sx={{ fontSize: 40, color: colors.amber[600] }} />
        ) : (
          <CasinoIcon sx={{ fontSize: 40, color: colors.primary.main }} />
        )}
      </Box>

      {/* Main Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          color: colors.slate[900],
          mb: 1.25,
          letterSpacing: "-0.02em",
          fontSize: { xs: "1.25rem", sm: "1.45rem" },
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body2"
        sx={{
          color: colors.slate[600],
          mb: 3,
          lineHeight: 1.6,
          maxWidth: 460,
          mx: "auto",
          fontSize: { xs: "0.875rem", sm: "0.925rem" },
        }}
      >
        {description}
      </Typography>

      {/* Context Information Pill */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          textAlign: "left",
          gap: 1.5,
          p: 2,
          mb: 3.5,
          bgcolor: colors.slate[50],
          border: `1px solid ${colors.slate[200]}`,
          borderRadius: borderRadiusPx.lg,
        }}
      >
        <InfoIcon sx={{ fontSize: 20, color: colors.primary.main, mt: 0.2, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: colors.slate[600], lineHeight: 1.5, fontSize: "0.8rem" }}>
          {lotteryId
            ? "You can review previous draw winners in the Results section or participate in other active lottery contracts."
            : "New lottery draws are generated automatically upon timer conclusion. Check back shortly or explore other game types."}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <CommonButton
          variant="play"
          size="medium"
          endIcon={<ArrowForwardIcon />}
          onClick={() => history.push(Path.allLotteries || "/all-lotteries")}
          sx={{ minWidth: { xs: "100%", sm: 200 } }}
        >
          Explore All Lotteries
        </CommonButton>

        <CommonButton
          variant="action"
          size="medium"
          startIcon={<TrophyIcon sx={{ fontSize: 18 }} />}
          onClick={() => history.push(Path.result || "/lottery-result")}
          sx={{ minWidth: { xs: "100%", sm: 170 } }}
        >
          View Results
        </CommonButton>
      </Stack>

      {/* Quick Game Mode Jump Shortcuts */}
      <Box
        sx={{
          pt: 2.5,
          borderTop: `1px dashed ${colors.slate[200]}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: colors.slate[400],
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            mb: 1.5,
            fontSize: "0.7rem",
          }}
        >
          Or Jump Directly to Game Arenas
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          {quickLinks.map((item, idx) => (
            <Chip
              key={idx}
              icon={item.icon}
              label={item.label}
              size="small"
              clickable
              onClick={() => history.push(item.path)}
              sx={{
                bgcolor: colors.slate[100],
                color: colors.slate[700],
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: borderRadiusPx.sm || "6px",
                border: `1px solid ${colors.slate[200]}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  borderColor: colors.blue[200],
                  transform: "translateY(-1px)",
                },
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

BuyLotteryEmptyState.propTypes = {
  lotteryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRetry: PropTypes.func,
};

export default BuyLotteryEmptyState;
